/**
 * The data bundle of a quote or contract PDF (TJR inventory 01 §3.4, §3.6, §10.3; ADR-017). One builder and one Typst
 * template (`crates/teslyn-core/templates/commercial-document.typ`) make the quote, the full contract and the
 * operator (OP) contract.
 *
 * - Section order and wording come from the tenant's clause template (`ClauseTemplate`); numbers (1, 2.1, 2.8.1) and
 *   cross-references `{{ref:<id>}}` are generated from the sections actually printed (bug B6).
 * - OP (MB decisions 120 and 165): today's OP contract with every amount and the bank details blank; everything
 *   else is shown, including the payment percentages. Amounts and bank details are never written into an OP bundle
 *   (they are blank here, not hidden by the template), so a template error cannot show them. The full contract (and
 *   invoices) keep the bank details.
 * - Text is passed to Typst as plain strings in runs (`**bold**` in clause text becomes a bold run); clause text is
 *   never evaluated as Typst markup.
 */
import {
  areaLineKm,
  detectorLitres,
  directionLabel,
  formatKmInt,
  formatMoney,
  paymentBalance,
  quoteTotals,
  sampleDistance,
} from './quote-calc.js';
import { formatPhoneAu } from './phone.js';
import { CONTRACT_OPERATOR_TAGS, CONTRACT_SIGNING_TAGS } from './docuseal.js';
import type {
  ClauseBlock,
  ClauseSection,
  ClauseTemplate,
  ClientContact,
  ClientOrgDetails,
  ContractSnapshot,
  GeneratedBlock,
  QuoteAggregate,
  TenantPaymentDetails,
  TenantProfile,
} from './quote-model.js';
import { AU_STATES, CURRENCY_LABEL } from './quote-model.js';
import type { QuoteOption } from './quote-model.js';
import { formatNumber, reflightSentences } from './qc-spec.js';
import { optionAggregate, optionTitle, optionsOf } from './quote-options.js';

export type Variant = 'quote' | 'full' | 'op';

export interface Run {
  readonly t: string;
  readonly b: boolean;
}

export type DocBlock =
  | { readonly kind: 'para'; readonly runs: readonly Run[] }
  | { readonly kind: 'label'; readonly text: string }
  | { readonly kind: 'bullets'; readonly items: ReadonlyArray<readonly Run[]> }
  | { readonly kind: 'kv'; readonly rows: ReadonlyArray<readonly [string, string]> }
  | {
      readonly kind: 'table';
      readonly head: readonly string[];
      readonly rows: ReadonlyArray<readonly string[]>;
      readonly foot: readonly string[] | null;
    }
  | { readonly kind: 'address'; readonly lines: readonly string[] }
  | {
      readonly kind: 'coords';
      readonly title: string;
      readonly lines: readonly string[];
      /** Column heads: point, then easting/northing or longitude/latitude. */
      readonly head: readonly [string, string, string];
      readonly rows: ReadonlyArray<readonly [string, string, string]>;
    }
  | {
      readonly kind: 'signing';
      readonly parties: readonly string[];
      readonly fields: readonly string[];
      /**
       * DocuSeal's text tags on the first party's (the client's) lines, one per field or null, when the contract is
       * sent to the client to sign from the portal (R191, `withClientSigningTags`). Absent on every other copy.
       */
      readonly client_tags?: readonly (string | null)[];
      /** The same on the second party's (the operator's) lines, for the manager who signs second (decision 1014). */
      readonly operator_tags?: readonly (string | null)[];
    };

export interface DocSection {
  readonly id: string;
  /** "2.1", "PART 2", or "" for an unnumbered section. */
  readonly number: string;
  readonly level: 0 | 1 | 2 | 3;
  readonly heading: string;
  readonly blocks: readonly DocBlock[];
}

export interface DocumentBundle {
  readonly kind: 'quote' | 'contract';
  readonly variant: Variant;
  /** Document title for the PDF metadata. */
  readonly title: string;
  readonly footer: string;
  readonly cover: {
    readonly heading: string;
    readonly client: string;
    readonly tenant: string;
    readonly tenant_sub: string;
    readonly rows: ReadonlyArray<readonly [string, string]>;
  } | null;
  readonly letter: {
    readonly date: string;
    readonly reference: string;
    readonly addressee: readonly string[];
    readonly re: string;
    readonly closing: string;
    readonly signatory: readonly string[];
  } | null;
  readonly sections: readonly DocSection[];
  /** Variant marker printed under the footer, e.g. "Operator copy: amounts not shown". */
  readonly marker: string;
}

export class DocumentTemplateError extends Error {
  override readonly name = 'DocumentTemplateError';
}

// ---------------------------------------------------------------- words and dates

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "7 October 2026" from 2026-10-07. */
export function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map((v) => Number.parseInt(v, 10));
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

function monthYear(ym: string): string {
  const [y, m] = ym.split('-').map((v) => Number.parseInt(v, 10));
  return y && m ? `${MONTHS[m - 1]} ${y}` : ym;
}

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** 20 → "twenty", 45 → "forty-five" (0–99; larger numbers stay as figures). */
export function numberWords(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 99) return String(n);
  if (n < 20) return ONES[n]!;
  const t = TENS[Math.floor(n / 10)]!;
  return n % 10 === 0 ? t : `${t}-${ONES[n % 10]}`;
}

/** "in the first week of October 2026" (contract 4.1) and "First week of October 2026" (2.1 schedule). */
export function estimatedStart(q: QuoteAggregate['quote']): {
  readonly sentence: string;
  readonly schedule: string;
} {
  const my = monthYear(q.est_start_month);
  const year = q.est_start_month.slice(0, 4);
  const quarters: Record<string, string> = { q1: 'first', q2: 'second', q3: 'third', q4: 'fourth' };
  let body: string;
  switch (q.est_start_qualifier) {
    case 'in':
      body = my;
      break;
    case 'first_week':
      body = `the first week of ${my}`;
      break;
    case 'early':
    case 'mid':
    case 'late':
      body = `${q.est_start_qualifier} ${my}`;
      break;
    default:
      body = `the ${quarters[q.est_start_qualifier]} quarter of ${year}`;
  }
  const schedule = body.replace(/^the /, '');
  return { sentence: `in ${body}`, schedule: schedule.charAt(0).toUpperCase() + schedule.slice(1) };
}

function joinAddress(c: ClientOrgDetails | null): string {
  if (!c) return '';
  return [c.address_line1, c.address_line2, c.suburb, c.state, c.country]
    .map((s) => s.trim().replace(/,+$/, ''))
    .filter(Boolean)
    .join(', ');
}

function stateName(s: string): string {
  const t = s.trim();
  return AU_STATES.find((x) => x.short.toLowerCase() === t.toLowerCase())?.name ?? t;
}

/** "State of Victoria" / "State", or "Northern Territory" / "Territory" (TJR `*!GovLaw`, `*!StaOrTer`). */
export function governingLaw(s: string): { readonly law: string; readonly kind: string } {
  const name = stateName(s);
  if (/territory$/i.test(name)) return { law: name, kind: 'Territory' };
  if (AU_STATES.some((x) => x.name === name)) return { law: `State of ${name}`, kind: 'State' };
  return { law: name, kind: 'jurisdiction' };
}

const hz = (rate: number): string => `${formatNumber(rate)} Hz (${formatNumber(1 / rate, 3)} sec)`;

/** "C208" from "Cessna 208"; initials otherwise. */
export function aircraftShort(type: string): string {
  const digits = /\d+/.exec(type)?.[0];
  const words = type.split(/\s+/).filter(Boolean);
  if (digits) return `${(words[0] ?? 'A').charAt(0).toUpperCase()}${digits}`;
  return words.map((w) => w.charAt(0).toUpperCase()).join('');
}

/** `F26048_Benz_Mining_Corp_C208_Contract_V2_OP.pdf`, the layout Thomson files today (TJR inventory 01 §10.3). */
export function documentFileName(agg: QuoteAggregate, jobNumber: string, variant: Variant): string {
  const client = (agg.client?.name ?? 'Client').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const ac = aircraftShort(agg.quote.aircraft_type || 'Aircraft');
  const rev = agg.quote.revision;
  if (variant === 'quote') return `${agg.quote.number}_${client}_${ac}_Quote_V${rev}.pdf`;
  return `${jobNumber}_${client}_${ac}_Contract_V${rev}${variant === 'op' ? '_OP' : ''}.pdf`;
}

// ---------------------------------------------------------------- text filling

/** `**bold**` to runs, with whitespace collapsed (a blank OP amount leaves no double space). */
export function toRuns(text: string): Run[] {
  const parts = text.split('**');
  const runs: Run[] = [];
  parts.forEach((t, i) => {
    if (t !== '') runs.push({ t, b: i % 2 === 1 });
  });
  // collapse spaces across run boundaries and drop empty runs
  const out: Run[] = [];
  let prevSpace = true;
  for (const r of runs) {
    let t = r.t.replace(/\s+/g, ' ');
    if (prevSpace) t = t.replace(/^ /, '');
    if (t === '') continue;
    prevSpace = t.endsWith(' ');
    const prev = out[out.length - 1];
    if (prev && prev.b === r.b) out[out.length - 1] = { t: prev.t + t, b: r.b };
    else out.push({ t, b: r.b });
  }
  const last = out[out.length - 1];
  if (last && last.t.endsWith(' ')) out[out.length - 1] = { t: last.t.replace(/ $/, ''), b: last.b };
  // " ." left by a blank value before a full stop
  return out.map((r) => ({ t: r.t.replace(/ ([.,;])/g, '$1'), b: r.b }));
}

interface Ctx {
  readonly variant: Variant;
  /** The first (or only) option's aggregate: what every shared text is filled from. */
  readonly agg: QuoteAggregate;
  /** Every option side by side (decision 171); one for a contract or a quote without a choice. */
  readonly options: ReadonlyArray<{ readonly option: QuoteOption; readonly agg: QuoteAggregate }>;
  readonly clauses: ClauseTemplate;
  readonly profile: TenantProfile;
  readonly payment: TenantPaymentDetails | null;
  readonly jobNumber: string;
  readonly contractDate: string;
  readonly values: Readonly<Record<string, string>>;
  readonly conditions: ReadonlySet<string>;
  readonly refs: ReadonlyMap<string, string>;
}

function fill(text: string, ctx: Ctx, extra: Readonly<Record<string, string>> = {}): string {
  let out = text.replace(/\{\{phrase:([\w.]+)\}\}/g, (_, k: string) => {
    const p = ctx.clauses.phrases[k];
    if (p === undefined)
      throw new DocumentTemplateError(`Clause phrase "${k}" is missing from ${ctx.clauses.version}.`);
    return p;
  });
  out = out.replace(/\{\{([\w.:]+)\}\}/g, (_, k: string) => {
    if (k.startsWith('ref:')) {
      const n = ctx.refs.get(k.slice(4));
      if (n === undefined)
        throw new DocumentTemplateError(`Cross-reference to "${k.slice(4)}", which is not printed.`);
      return n;
    }
    const v = extra[k] ?? ctx.values[k];
    if (v === undefined)
      throw new DocumentTemplateError(`Unknown placeholder {{${k}}} in ${ctx.clauses.version}.`);
    return v;
  });
  return out;
}

const phrase = (ctx: Ctx, key: string, extra: Readonly<Record<string, string>> = {}): string => {
  const p = ctx.clauses.phrases[key];
  if (p === undefined)
    throw new DocumentTemplateError(`Clause phrase "${key}" is missing from ${ctx.clauses.version}.`);
  return fill(p, ctx, extra);
};

/** `mag`, `!op`, `rad & pricing`… true when every term holds. */
export function conditionHolds(when: string | null | undefined, conditions: ReadonlySet<string>): boolean {
  if (!when) return true;
  return when
    .split('&')
    .map((s) => s.trim())
    .every((term) => (term.startsWith('!') ? !conditions.has(term.slice(1)) : conditions.has(term)));
}

// ---------------------------------------------------------------- values and conditions

function money(ctx: Pick<Ctx, 'variant' | 'agg'>, cents: number | null): string {
  if (ctx.variant === 'op' || cents === null) return '';
  return formatMoney(cents, ctx.agg.quote.currency);
}

function buildValues(
  variant: Variant,
  agg: QuoteAggregate,
  clauses: ClauseTemplate,
  profile: TenantProfile,
  jobNumber: string,
  contractDate: string,
): Record<string, string> {
  const q = agg.quote;
  const s = agg.sampling;
  const t = agg.terms;
  const totals = quoteTotals(agg, profile);
  const ctx = { variant, agg };
  const start = estimatedStart(q);
  const gov = governingLaw(q.governing_law);
  const magDist =
    s.mag_rate_hz && q.aircraft_cruise_kt ? sampleDistance(q.aircraft_cruise_kt, s.mag_rate_hz) : null;
  const radDist =
    s.rad_rate_hz && q.aircraft_cruise_kt ? sampleDistance(q.aircraft_cruise_kt, s.rad_rate_hz) : null;
  const duration =
    q.est_duration_value === null
      ? 'to be advised'
      : `${formatNumber(q.est_duration_value)} ${q.est_duration_value === 1 ? q.est_duration_unit.replace(/s$/, '') : q.est_duration_unit}` +
        (q.est_duration_note.trim() ? ` ${q.est_duration_note.trim()}` : '');
  const wd = q.delivery_working_days;
  const instruments = [
    s.rad_rate_hz !== null ? 'spectrometer,' : '',
    s.mag_rate_hz !== null ? 'magnetometer,' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const surveyType = [s.mag_rate_hz !== null ? 'magnetic' : '', s.rad_rate_hz !== null ? 'radiometric' : '']
    .filter(Boolean)
    .join(' and ');
  const params = Object.fromEntries(Object.entries(clauses.params).map(([k, v]) => [k, String(v)]));
  const standby = money(ctx, totals.standby_per_day_cents);
  return {
    ...params,
    T: profile.short_name,
    'tenant.legal_name': profile.legal_name,
    'tenant.abn': profile.abn,
    'tenant.incorporated_in': profile.incorporated_in,
    'tenant.registered_office': profile.registered_office,
    date: longDate(contractDate),
    'client.name': agg.client?.name ?? '',
    'client.legal_name': agg.client?.legal_name || agg.client?.name || '',
    'client.address': joinAddress(agg.client),
    project: q.project_name,
    located_in: q.located.trim() ? `${q.located.trim()} in ${q.location}` : `in ${q.location}`,
    survey_type: surveyType,
    aircraft: q.aircraft_type,
    job: jobNumber,
    quote_number: q.number,
    est_start: start.sentence,
    duration,
    working_days:
      wd === null ? 'the agreed number of working days' : `${numberWords(wd)} (${wd}) working days`,
    currency_name: CURRENCY_LABEL[q.currency].name,
    standby_rate: standby,
    standby_rate_per_day: standby ? `${standby} per day` : '',
    standby_cap: totals.standby_cap_days === null ? '' : `${totals.standby_cap_days} days`,
    tasp: money(ctx, totals.tasp_estimate_cents),
    gov_law: gov.law,
    gov_law_kind: gov.kind,
    base_station: s.base_station || 'a base station magnetometer',
    instruments,
    magnetometers: String(s.magnetometers),
    mag_rate: s.mag_rate_hz ? hz(s.mag_rate_hz) : '',
    rad_rate: s.rad_rate_hz ? hz(s.rad_rate_hz) : '',
    mag_hz: s.mag_rate_hz ? formatNumber(s.mag_rate_hz) : '',
    rad_hz: s.rad_rate_hz ? formatNumber(s.rad_rate_hz) : '',
    mag_dist: magDist ? `${magDist.low}-${magDist.high}` : '',
    rad_dist: radDist ? `${radDist.low}-${radDist.high}` : '',
    packs: String(s.spectrometer_packs),
    litres: formatNumber(detectorLitres(s.spectrometer_packs), 1),
    payment_days: String(t.payment_days),
    late_fee: formatNumber(t.late_fee_pct_month),
    pp1: t.pp1_pct === null ? '' : `${t.pp1_pct}%`,
    pp2: t.pp2_pct === null ? '' : `${t.pp2_pct}%`,
  };
}

function buildConditions(variant: Variant, agg: QuoteAggregate, profile: TenantProfile): Set<string> {
  const s = agg.sampling;
  const c = new Set<string>();
  if (variant === 'op') c.add('op');
  if (variant !== 'op') c.add('pricing');
  if (s.mag_rate_hz !== null) c.add('mag');
  if (s.rad_rate_hz !== null) c.add('rad');
  if (s.video) c.add('video');
  if (s.upward_detector) c.add('upward');
  if (s.ground_concentrations) c.add('ground_concentrations');
  if (agg.quote.fuel_by === 'client') c.add('fuel_client');
  const tasp = agg.charges.find((x) => x.kind === 'tasp');
  if (agg.quote.permits_by === 'thomson_except_tasp' || (tasp && tasp.amount !== null)) c.add('tasp');
  if (agg.quote.free_grid_offer) c.add('grid_offer');
  if (agg.quote.print_boundary_coords) c.add('boundary_coords');
  if (profile.tax_line_enabled) c.add('tax');
  return c;
}

// ---------------------------------------------------------------- numbering

function numberSections(
  kind: 'quote' | 'contract',
  sections: readonly ClauseSection[],
  conditions: ReadonlySet<string>,
): Array<{ readonly section: ClauseSection; readonly number: string }> {
  const counters = [0, 0, 0];
  const out: Array<{ section: ClauseSection; number: string }> = [];
  for (const section of sections) {
    if (!conditionHolds(section.when, conditions)) continue;
    let number = '';
    if (section.level >= 1) {
      const i = section.level - 1;
      counters[i] = (counters[i] ?? 0) + 1;
      for (let j = i + 1; j < 3; j += 1) counters[j] = 0;
      number = counters.slice(0, section.level).join('.');
      if (kind === 'quote' && section.level === 1) number = `PART ${counters[0]}`;
    }
    out.push({ section, number });
  }
  return out;
}

// ---------------------------------------------------------------- generated blocks

function contactLines(c: ClientContact | null, org: ClientOrgDetails | null, withPhone: boolean): string[] {
  const lines: string[] = [];
  if (c) {
    lines.push(c.name);
    if (c.position.trim()) lines.push(c.position.trim());
    // decision 672: grouped as Australians write them ("0409 966 248"), as the weekly report's cover does
    if (withPhone && c.mobile.trim()) lines.push(`M: ${formatPhoneAu(c.mobile.trim())}`);
    if (withPhone && c.phone.trim()) lines.push(`P: ${formatPhoneAu(c.phone.trim())}`);
    if (c.email.trim()) lines.push(`E: ${c.email.trim()}`);
  }
  if (org) {
    lines.push(org.legal_name || org.name);
    const addr = joinAddress(org);
    if (addr) lines.push(addr);
    if (org.web.trim()) lines.push(org.web.trim());
  }
  return lines;
}

function areasTable(agg: QuoteAggregate): DocBlock {
  const areas = [...agg.areas].sort((a, b) => a.sort - b.sort);
  const total = areas.reduce((sum, a) => sum + areaLineKm(a), 0);
  return {
    kind: 'table',
    head: [
      'Area Name',
      'Traverse Line Spacing & Direction',
      'Tie Line Spacing & Direction',
      'Survey (Sensor) Height',
      'Line Km',
    ],
    rows: areas.map((a) => [
      a.name,
      `${formatNumber(a.trav_spacing_m)}m ${directionLabel(a.trav_direction_deg)}`,
      `${formatNumber(a.tie_spacing_m)}m ${directionLabel(a.tie_direction_deg)}`,
      `${formatNumber(a.sensor_height_m)}m`,
      formatKmInt(areaLineKm(a)),
    ]),
    foot: ['Total', '-', '-', '-', formatKmInt(total)],
  };
}

function surveySpecs(ctx: Ctx): DocBlock[] {
  const { agg } = ctx;
  const q = agg.quote;
  const s = agg.sampling;
  const out: DocBlock[] = [];
  {
    const params: string[] = [];
    if (s.mag_rate_hz !== null)
      params.push(`Magnetics ${s.magnetometers} sensor${s.magnetometers === 1 ? '' : 's'}`);
    if (s.rad_rate_hz !== null) {
      params.push(
        `Radiometrics (${s.spectrometer_packs} packs = ${formatNumber(detectorLitres(s.spectrometer_packs), 1)} litres)`,
      );
    }
    const rates: string[] = [];
    const sample = (rate: number | null): void => {
      if (rate === null) return;
      const d = q.aircraft_cruise_kt ? sampleDistance(q.aircraft_cruise_kt, rate) : null;
      rates.push(d ? `${hz(rate)} (approximately ${d.low}m samples)` : hz(rate));
    };
    sample(s.mag_rate_hz);
    sample(s.rad_rate_hz);
    out.push({
      kind: 'kv',
      rows: [
        ['Project Name', q.project_name],
        ['Survey Schedule (TBC)', estimatedStart(q).schedule],
        ['Proposed Aircraft', ctx.values['aircraft'] ?? q.aircraft_type],
        ['Proposed Base', q.bases.join(', ')],
        ['Parameters Measured', params.join('; ')],
        ['Sample Rate', rates.join('; ')],
      ],
    });
  }
  if (ctx.options.length === 1) {
    out.push(areasTable(agg));
  } else {
    // decision 171: each option's areas side by side
    for (const o of ctx.options) {
      out.push({ kind: 'label', text: `${optionTitle(o.option)} (${o.agg.quote.aircraft_type})` });
      out.push(areasTable(o.agg));
    }
  }
  return out;
}

/**
 * Section 2.2: each area's boundary table with its own datum (decision 170), as the client supplied it: grid
 * coordinates in whole metres (as the OP prints them) or longitude and latitude to 8 decimals (as the KML gives them).
 * An area without a table prints its UTM boundary in the quote's boundary datum.
 */
function areaCoordinates(ctx: Ctx): DocBlock[] {
  const out: DocBlock[] = [];
  // every option's areas; an area copied into several options is printed once
  const seen = new Set<string>();
  const all = ctx.options.flatMap((o) => [...o.agg.areas].sort((x, y) => x.sort - y.sort));
  for (const a of all) {
    const key = JSON.stringify([a.name, a.boundary_table ?? a.boundary]);
    if (seen.has(key)) continue;
    seen.add(key);
    const t =
      a.boundary_table ??
      (a.boundary.length > 0
        ? {
            datum: ctx.agg.quote.coords_datum || ctx.agg.sampling.datum,
            kind: 'grid' as const,
            zone: a.zone,
            points: a.boundary,
            source: '',
          }
        : null);
    if (!t || t.points.length === 0) continue;
    const grid = t.kind === 'grid';
    out.push({
      kind: 'coords',
      title: `${a.name} Survey Area`,
      lines: [
        `Area Name: ${a.name}`,
        `Coordinates in ${t.datum}`,
        grid
          ? `Grid Zone: ${t.zone === null ? 'not set' : String(t.zone)}`
          : 'Longitude and latitude in decimal degrees',
      ],
      head: grid ? ['#', 'Easting', 'Northing'] : ['#', 'Longitude', 'Latitude'],
      rows: t.points.map((p, i) =>
        grid
          ? ([String(i + 1), String(Math.trunc(p[0])), String(Math.trunc(p[1]))] as const)
          : ([String(i + 1), p[0].toFixed(8), p[1].toFixed(8)] as const),
      ),
    });
  }
  return out;
}

function conductItems(ctx: Ctx): DocBlock[] {
  const q = ctx.agg.quote;
  const has = (k: string): boolean => (q.conduct_items as readonly string[]).includes(k);
  const keys: string[] = [];
  if (has('personnel_equipment')) keys.push('conduct.personnel_equipment');
  if (q.fuel_by === 'thomson') keys.push('conduct.fuel_thomson');
  if (q.fuel_by === 'client') keys.push('conduct.fuel_client');
  if (has('fuel_truck')) keys.push('conduct.fuel_truck');
  if (has('fuel_positioning')) keys.push('conduct.fuel_positioning');
  if (q.permits_by === 'thomson_all') keys.push('conduct.permits_all');
  if (q.permits_by === 'thomson_except_tasp') keys.push('conduct.permits_except_tasp');
  for (const k of ['accommodation', 'meals', 'transport', 'supplies']) if (has(k)) keys.push(`conduct.${k}`);
  if (keys.length === 0) return [];
  const items = keys.map((k, i) => {
    const end = i === keys.length - 1 ? '.' : i === keys.length - 2 ? '; and' : ';';
    return toRuns(phrase(ctx, k) + end);
  });
  return [{ kind: 'bullets', items }];
}

function locatedFiles(ctx: Ctx): DocBlock[] {
  const s = ctx.agg.sampling;
  const out: DocBlock[] = [];
  for (const d of [...ctx.agg.deliverables].sort((a, b) => a.sort - b.sort)) {
    if (d.kind === 'located') {
      out.push({ kind: 'label', text: d.contents.trim() ? `${d.label} (${d.contents.trim()})` : d.label });
      if (d.method !== 'none') {
        const fmt = d.method === 'mag' ? s.mag_located_format : s.rad_located_format;
        out.push({
          kind: 'para',
          runs: toRuns(
            fmt.trim()
              ? phrase(ctx, `located.${d.method}_intro`, { format: fmt.trim() })
              : phrase(ctx, `located.${d.method}_intro_any`),
          ),
        });
      }
    } else {
      const fmt = d.method === 'rad' ? s.rad_grid_format : s.mag_grid_format;
      out.push({
        kind: 'para',
        runs: toRuns(
          fmt.trim() ? phrase(ctx, 'grid.intro', { format: fmt.trim() }) : phrase(ctx, 'grid.intro_any'),
        ),
      });
    }
    if (d.items.length > 0) out.push({ kind: 'bullets', items: d.items.map((i) => toRuns(i)) });
  }
  return out;
}

function chargesIntro(ctx: Ctx): DocBlock[] {
  const key = ctx.agg.quote.currency === 'AUD' ? 'price.aud' : 'price.other';
  const out: DocBlock[] = [{ kind: 'para', runs: toRuns(phrase(ctx, key)) }];
  if (ctx.conditions.has('tax') && ctx.variant !== 'op') {
    out.push({
      kind: 'para',
      runs: toRuns(
        `${ctx.profile.tax_label} at ${formatNumber(ctx.profile.tax_rate_pct)}% is shown separately below.`,
      ),
    });
  }
  return out;
}

/**
 * The price table. With several options (decision 171): first the options side by side, each with its own line km
 * and total, then each option's own breakdown; the client chooses one.
 */
function quoteAreas(ctx: Ctx): DocBlock[] {
  if (ctx.options.length === 1) return quoteAreasFor(ctx, ctx.agg);
  const m = (c: number | null): string => money(ctx, c);
  const rows = ctx.options.map((o) => {
    const t = quoteTotals(o.agg, ctx.profile);
    return [
      `Option ${o.option.label}`,
      o.option.description,
      o.agg.quote.aircraft_type,
      formatKmInt(t.total_line_km),
      m(t.total_ex_tax_cents),
      m(t.standby_per_day_cents),
    ];
  });
  const out: DocBlock[] = [
    {
      kind: 'para',
      runs: toRuns(
        `This proposal offers **${ctx.options.length} options**. Each option is priced on its own below; the Client chooses one option, and the Agreement is made for that option only.`,
      ),
    },
    {
      kind: 'table',
      head: ['Option', 'Description', 'Aircraft', 'Line km', 'Estimated total price', 'Daily standby'],
      rows,
      foot: null,
    },
  ];
  for (const o of ctx.options) {
    out.push({ kind: 'label', text: optionTitle(o.option) });
    out.push(...quoteAreasFor(ctx, o.agg));
  }
  return out;
}

function quoteAreasFor(ctx: Ctx, agg: QuoteAggregate): DocBlock[] {
  const totals = quoteTotals(agg, ctx.profile);
  const m = (c: number | null): string => money(ctx, c);
  const areas = [...agg.areas].sort((a, b) => a.sort - b.sort);
  const names = areas.map((a) => a.name);
  const areaWords =
    names.length <= 1 ? `${names[0] ?? 'The survey'} area` : `${agg.quote.project_name} areas`;
  const rows: Array<[string, string]> = [
    ['Mobilisation and Demobilisation:', m(totals.mobilisation_cents)],
    ['Data Acquisition and Data Processing:', m(totals.acquisition_cents)],
  ];
  const out: DocBlock[] = [{ kind: 'kv', rows }];
  const ref = ctx.refs.get('general_specs');
  out.push({
    kind: 'para',
    runs: toRuns(
      `${areaWords}${ref ? ` as described in ${ref}` : ''} containing approximately ${formatKmInt(totals.total_line_km)} line km`,
    ),
  });
  out.push({
    kind: 'table',
    head: ['Area', 'Line km', 'Rate per line km', 'Price'],
    rows: totals.areas.map((a) => [a.name, formatKmInt(a.line_km), m(a.rate_cents), m(a.price_cents)]),
    foot: ['Total', formatKmInt(totals.total_line_km), '', m(totals.acquisition_cents)],
  });
  const tail: Array<[string, string]> = [];
  tail.push([`Total approximately ${formatKmInt(totals.total_line_km)} line km`, '']);
  tail.push([
    'Estimated total price for this survey excluding applicable standby:',
    m(totals.total_ex_tax_cents),
  ]);
  if (ctx.conditions.has('tax')) {
    tail.push([
      `${ctx.profile.tax_label} (${formatNumber(ctx.profile.tax_rate_pct)}%):`,
      m(totals.tax_cents),
    ]);
    tail.push([`Estimated total price including ${ctx.profile.tax_label}:`, m(totals.total_inc_tax_cents)]);
  }
  if (ctx.variant === 'quote') {
    tail.push([
      'Daily standby rate:',
      m(totals.standby_per_day_cents) ? `${m(totals.standby_per_day_cents)} per day` : '',
    ]);
  }
  out.push({ kind: 'kv', rows: tail });
  // decision 167: data merge is a separate optional item, never in the total
  if (totals.data_merge_cents !== null) {
    out.push({ kind: 'label', text: 'Optional item (not included in the estimated total price)' });
    out.push({
      kind: 'kv',
      rows: [
        [
          'Data merge (merging the data of this survey with the existing survey data set):',
          m(totals.data_merge_cents),
        ],
      ],
    });
  }
  return out;
}

function standbyCap(ctx: Ctx): DocBlock[] {
  const out: DocBlock[] = [];
  const standby = ctx.agg.charges.find((c) => c.kind === 'standby');
  if (ctx.options.length > 1) {
    // the cap and note of each option, when they differ between options
    const caps = ctx.options.map((o) => o.agg.charges.find((c) => c.kind === 'standby'));
    const same = caps.every(
      (c) =>
        (c?.cap_days ?? null) === (standby?.cap_days ?? null) && (c?.note ?? '') === (standby?.note ?? ''),
    );
    if (!same) {
      ctx.options.forEach((o, i) => {
        const c = caps[i];
        if (c?.cap_days !== null && c?.cap_days !== undefined) {
          out.push({
            kind: 'para',
            runs: toRuns(`${optionTitle(o.option)}: standby charges will not exceed ${c.cap_days} days.`),
          });
        }
        if (c?.note.trim())
          out.push({ kind: 'para', runs: toRuns(`${optionTitle(o.option)}: ${c.note.trim()}`) });
      });
      return out;
    }
  }
  if (standby?.cap_days !== null && standby?.cap_days !== undefined) {
    out.push({ kind: 'para', runs: toRuns(phrase(ctx, 'standby.cap')) });
  }
  if (standby?.note.trim()) out.push({ kind: 'para', runs: toRuns(standby.note.trim()) });
  return out;
}

/** The payment parts actually due: a 0 % part (decision 168, e.g. 50/50/0) is not printed. */
function paymentSchedule(ctx: Ctx): DocBlock[] {
  const t = ctx.agg.terms;
  const items: Run[][] = [];
  if (t.pp1_pct !== null && t.pp1_pct > 0) items.push(toRuns(phrase(ctx, 'pay.first')));
  if (t.pp2_pct !== null && t.pp2_pct > 0) items.push(toRuns(phrase(ctx, 'pay.second')));
  const balance = t.balance_pct ?? paymentBalance(t.pp1_pct, t.pp2_pct);
  if (balance !== null && balance > 0) items.push(toRuns(phrase(ctx, 'pay.balance')));
  return items.length ? [{ kind: 'bullets', items }] : [];
}

function payee(ctx: Ctx): DocBlock[] {
  const p = ctx.payment;
  if (!p)
    return [
      {
        kind: 'para',
        runs: toRuns('Payment details are not set up yet. The tenant admin enters them in Settings.'),
      },
    ];
  return [{ kind: 'address', lines: [ctx.profile.legal_name, ...p.payee_address.filter((l) => l.trim())] }];
}

/** The bank account rows; on the OP contract every value is blank (decision 165), the labels stay. */
function bankAccount(ctx: Ctx): DocBlock[] {
  const p = ctx.payment;
  if (!p) return [];
  const op = ctx.variant === 'op';
  const v = (value: string): string => (op ? '' : value);
  return [
    {
      kind: 'kv',
      rows: [
        ['A/C Name', v(p.account_name)],
        ['Bank', v(p.bank_name)],
        ['Address', v(p.bank_address)],
        ['BSB', v(p.bsb)],
        ['Account No', v(p.account_number)],
        ['SWIFT Code', v(p.swift)],
      ],
    },
  ];
}

function noticesAddresses(ctx: Ctx): DocBlock[] {
  const pr = ctx.profile;
  const tenant = [pr.legal_name, ...pr.notices_address.filter((l) => l.trim())];
  if (pr.notices_attention.trim()) tenant.push(`Attention: ${pr.notices_attention.trim()}`);
  if (pr.notices_email.trim()) tenant.push(`Email: ${pr.notices_email.trim()}`);
  const n = ctx.agg.notices ?? ctx.agg.principal;
  const org = ctx.agg.client;
  const client = [org?.legal_name || org?.name || 'Client'];
  const addr = joinAddress(org);
  if (addr) client.push(addr);
  if (n) {
    client.push(`Attention: ${n.name}`);
    if (n.email.trim()) client.push(`Email: ${n.email.trim()}`);
  }
  return [
    { kind: 'address', lines: tenant },
    { kind: 'address', lines: client },
  ];
}

function signing(ctx: Ctx): DocBlock[] {
  const client = ctx.agg.client?.legal_name || ctx.agg.client?.name || 'Client';
  return [
    {
      kind: 'signing',
      parties: [`${client} - ${ctx.jobNumber}`, ctx.profile.legal_name],
      fields: ['Signed', 'Print Name', 'Position', 'WITNESS', 'Signed', 'Print Name', 'Date'],
    },
  ];
}

function generated(name: GeneratedBlock, ctx: Ctx): DocBlock[] {
  switch (name) {
    case 'party_sentence':
      return [{ kind: 'para', runs: toRuns(phrase(ctx, 'party')) }];
    case 'conduct_items':
      return conductItems(ctx);
    case 'survey_specs':
      return surveySpecs(ctx);
    case 'area_coordinates':
      return areaCoordinates(ctx);
    case 'principal_contact':
      return [{ kind: 'address', lines: contactLines(ctx.agg.principal, ctx.agg.client, true) }];
    case 'equipment_mag':
      return [{ kind: 'para', runs: toRuns(phrase(ctx, 'equipment.mag')) }];
    case 'equipment_rad':
      return [{ kind: 'para', runs: toRuns(phrase(ctx, 'equipment.rad')) }];
    case 'equipment_bullets':
      return [];
    case 'reflight_rules':
      return [
        { kind: 'bullets', items: reflightSentences(ctx.agg.qc, ctx.clauses.phrases).map((s) => toRuns(s)) },
      ];
    case 'located_files':
      return locatedFiles(ctx);
    case 'charges':
      return chargesIntro(ctx);
    case 'quote_areas':
      return quoteAreas(ctx);
    case 'standby_cap':
      return standbyCap(ctx);
    case 'payment_schedule':
      return paymentSchedule(ctx);
    case 'payee':
      return payee(ctx);
    case 'bank_account':
      return bankAccount(ctx);
    case 'notices_addresses':
      return noticesAddresses(ctx);
    case 'free_grid_offer': {
      const list = ctx.clauses.lists?.['free_grid_offer'] ?? [];
      return [
        { kind: 'para', runs: toRuns(phrase(ctx, 'grid_offer.intro')) },
        { kind: 'bullets', items: list.map((i) => toRuns(i)) },
      ];
    }
    case 'signing':
      return signing(ctx);
  }
}

function blockOut(b: ClauseBlock, ctx: Ctx): DocBlock[] {
  if (!conditionHolds(b.when, ctx.conditions)) return [];
  if ('p' in b) {
    const text = fill(b.p, ctx);
    return text.trim() ? [{ kind: 'para', runs: toRuns(text) }] : [];
  }
  if ('bullets' in b) return [{ kind: 'bullets', items: b.bullets.map((t) => toRuns(fill(t, ctx))) }];
  if ('label' in b) return [{ kind: 'label', text: fill(b.label, ctx) }];
  if ('kv' in b) return [{ kind: 'kv', rows: b.kv.map(([k, v]) => [fill(k, ctx), fill(v, ctx)] as const) }];
  return generated(b.generated, ctx);
}

// ---------------------------------------------------------------- public builders

export interface BuildInput {
  readonly agg: QuoteAggregate;
  readonly clauses: ClauseTemplate;
  readonly profile: TenantProfile;
  /** Needed for the full contract; the OP contract prints the bank details blank (decision 165); never a quote. */
  readonly payment: TenantPaymentDetails | null;
  readonly jobNumber: string;
  /** The date in the party sentence (YYYY-MM-DD). */
  readonly contractDate: string;
}

/** One value when every option gives the same, else the words of `differs`. */
function sameOr(values: readonly string[], differs: string): string {
  return values.every((v) => v === values[0]) ? (values[0] ?? '') : differs;
}

function build(kind: 'quote' | 'contract', variant: Variant, input: BuildInput): DocumentBundle {
  const { clauses, profile } = input;
  if (clauses.kind !== kind)
    throw new DocumentTemplateError(`A ${kind} needs a ${kind} clause template, not ${clauses.kind}.`);
  // decision 171: a contract is made from one option; a quote shows every option
  const options = optionsOf(input.agg).map((option) => ({
    option,
    agg: optionAggregate(input.agg, option.id),
  }));
  if (kind === 'contract' && options.length > 1) {
    throw new DocumentTemplateError('A contract is made from the one option the client chose.');
  }
  const agg = options[0]!.agg;
  const conditions = new Set(options.flatMap((o) => [...buildConditions(variant, o.agg, profile)]));
  const numbered = numberSections(kind, clauses.sections, conditions);
  const refs = new Map(
    numbered.filter((n) => n.number).map((n) => [n.section.id, n.number.replace(/^PART /, 'Part ')]),
  );
  const values = buildValues(variant, agg, clauses, profile, input.jobNumber, input.contractDate);
  if (options.length > 1) {
    const each = options.map((o) =>
      buildValues(variant, o.agg, clauses, profile, input.jobNumber, input.contractDate),
    );
    const aircraft = [...new Set(options.map((o) => o.agg.quote.aircraft_type))];
    Object.assign(values, {
      aircraft: aircraft.join(' or '),
      tasp: sameOr(
        each.map((v) => v['tasp'] ?? ''),
        'the amount shown for each option',
      ),
      standby_rate: sameOr(
        each.map((v) => v['standby_rate'] ?? ''),
        'the rate shown for each option',
      ),
      standby_rate_per_day: sameOr(
        each.map((v) => v['standby_rate_per_day'] ?? ''),
        'the daily rate shown for each option',
      ),
      standby_cap: sameOr(
        each.map((v) => v['standby_cap'] ?? ''),
        'the days shown for each option',
      ),
    });
  }
  const ctx: Ctx = {
    variant,
    agg,
    options,
    clauses,
    profile,
    payment: kind === 'contract' ? input.payment : null,
    jobNumber: input.jobNumber,
    contractDate: input.contractDate,
    values,
    conditions,
    refs,
  };
  const sections: DocSection[] = numbered.map(({ section, number }) => ({
    id: section.id,
    number,
    level: section.level,
    heading: section.heading ? fill(section.heading, ctx) : '',
    blocks: section.blocks.flatMap((b) => blockOut(b, ctx)),
  }));
  // the quote as a whole (its own aircraft type and every option) names the file and the letter
  const q = input.agg.quote;
  const client = agg.client?.name ?? '';
  const footerRef =
    kind === 'quote'
      ? documentFileName(input.agg, input.jobNumber, 'quote')
      : documentFileName(input.agg, input.jobNumber, variant);
  const footer = [footerRef.replace(/\.pdf$/, ''), profile.footer].filter(Boolean).join(' · ');
  if (kind === 'quote') {
    const p = agg.principal;
    const addressee = [
      p?.name ?? '',
      p?.position ?? '',
      agg.client?.legal_name || client,
      joinAddress(agg.client),
    ].filter((l) => l.trim());
    return {
      kind,
      variant,
      title: `${q.number} ${client} quotation`,
      footer,
      cover: null,
      letter: {
        date: longDate(q.quote_date),
        reference: q.number,
        addressee,
        re: `RE: ${q.project_name} - Airborne Geophysical Survey`,
        closing: 'Yours sincerely,',
        signatory: [q.signatory_name, q.signatory_title, profile.legal_name].filter((l) => l.trim()),
      },
      sections,
      marker: '',
    };
  }
  return {
    kind,
    variant,
    title: `${input.jobNumber} ${client} survey agreement${variant === 'op' ? ' (OP)' : ''}`,
    footer,
    cover: {
      heading: 'AIRBORNE GEOPHYSICAL SURVEY AGREEMENT',
      client: (agg.client?.legal_name || client).toUpperCase(),
      tenant: profile.legal_name.toUpperCase(),
      tenant_sub: profile.short_name,
      rows: [
        ['Job Reference', input.jobNumber],
        ['Quotation Reference', q.number],
        ['Project Name', q.project_name],
        ['Location', q.location],
        ['Country', q.country],
        ['Date', longDate(q.quote_date)],
      ],
    },
    letter: null,
    sections,
    marker: variant === 'op' ? 'OP' : '',
  };
}

export function buildQuoteDocument(
  input: Omit<BuildInput, 'payment' | 'jobNumber' | 'contractDate'>,
): DocumentBundle {
  return build('quote', 'quote', {
    ...input,
    payment: null,
    jobNumber: '',
    contractDate: input.agg.quote.quote_date,
  });
}

export function buildContractDocument(
  variant: 'full' | 'op',
  snapshot: ContractSnapshot,
  input: {
    readonly profile: TenantProfile;
    readonly payment: TenantPaymentDetails | null;
    readonly jobNumber: string;
    readonly contractDate: string;
  },
): DocumentBundle {
  return build('contract', variant, { agg: snapshot.quote, clauses: snapshot.clauses, ...input });
}

/**
 * The contract as it is signed through DocuSeal (R191, decision 1010 item 2; decision 854; decision 1014: fully
 * electronic): the same bundle with DocuSeal's text tags on the client's lines of the signing page
 * (`CONTRACT_SIGNING_TAGS`) and on the operator's lines for the manager who signs second (`CONTRACT_OPERATOR_TAGS`),
 * white 4 pt text DocuSeal removes. The witness lines stay blank, as on paper.
 */
export function withClientSigningTags(bundle: DocumentBundle): DocumentBundle {
  return {
    ...bundle,
    sections: bundle.sections.map((s) => ({
      ...s,
      blocks: s.blocks.map((b) =>
        b.kind === 'signing'
          ? {
              ...b,
              client_tags: b.fields.map((_, i) => CONTRACT_SIGNING_TAGS[i] ?? null),
              ...(b.parties.length > 1
                ? { operator_tags: b.fields.map((_, i) => CONTRACT_OPERATOR_TAGS[i] ?? null) }
                : {}),
            }
          : b,
      ),
    })),
  };
}

/** Whether a bundle carries the client's DocuSeal tags (the copy sent to the client to sign). */
export const hasClientSigningTags = (bundle: DocumentBundle): boolean =>
  bundle.sections.some((s) => s.blocks.some((b) => b.kind === 'signing' && (b.client_tags?.length ?? 0) > 0));

/** Every text of a bundle, for tests and search (amount checks of AC-C3). */
export function bundleText(b: DocumentBundle): string {
  const parts: string[] = [b.title, b.footer];
  if (b.cover) parts.push(b.cover.heading, b.cover.client, b.cover.tenant, ...b.cover.rows.flat());
  if (b.letter)
    parts.push(b.letter.date, b.letter.reference, ...b.letter.addressee, b.letter.re, ...b.letter.signatory);
  for (const s of b.sections) {
    parts.push(s.number, s.heading);
    for (const bl of s.blocks) {
      switch (bl.kind) {
        case 'para':
          parts.push(bl.runs.map((r) => r.t).join(''));
          break;
        case 'label':
          parts.push(bl.text);
          break;
        case 'bullets':
          for (const i of bl.items) parts.push(i.map((r) => r.t).join(''));
          break;
        case 'kv':
          for (const [k, v] of bl.rows) parts.push(k, v);
          break;
        case 'table':
          parts.push(...bl.head, ...bl.rows.flat(), ...(bl.foot ?? []));
          break;
        case 'address':
          parts.push(...bl.lines);
          break;
        case 'coords':
          parts.push(bl.title, ...bl.lines, ...bl.head, ...bl.rows.flat());
          break;
        case 'signing':
          parts.push(...bl.parties, ...bl.fields);
          break;
      }
    }
  }
  return parts.join('\n');
}
